import { useMemo, useState, useEffect } from "react";
import {
  AppShell,
  Button,
  Paper,
  Text,
  Pill,
  Popover,
  Drawer,
  Stack,
  Group,
  Menu,
} from "@mantine/core";
import type { IconProps } from "@tabler/icons-react";
import {
  IconChevronRight,
  IconPlus,
  IconPencil,
  IconDownload,
  IconChevronDown,
} from "@tabler/icons-react";
import MainHeader from "../../../components/Tax Client Only/MainHeader/MainHeader";
import { useDisclosure, useViewportSize } from "@mantine/hooks";
import { useNavigate } from "react-router-dom";
import "./TaxOrganizerHome.scss";
import { useTaxOrganizerListStore } from "@utils/stores/taxOrganizerListStore";
import Sidebar from "@components/Sidebar/Sidebar";
import MobileButtons from "@components/Tax Client Only/MobileButtons/MobileButtons";
import { generateTaxYear } from "@/utilities";
import { OrganizerStatus, statusConfigs } from "@constants/TaxOrganizer";
import TaxOrganizerSkeleton from "./TaxOrganizerSkeleton";
import { AppRoutes } from "@/constants.ts";
import { useAuthStore } from "@utils/stores/authStore";
import { useClientExport } from "@/utils/hooks/useClientExport";

interface BadgeProps {
  icon: React.ElementType<IconProps>;
  text: string;
  color?: string;
  size?: "sm" | "md" | "lg";
  backgroundColor?: string;
}

const Badge = ({
  icon: Icon,
  text,
  color,
  backgroundColor,
  size = "md",
}: BadgeProps) => (
  <Pill
    style={{
      backgroundColor: backgroundColor,
      color: color,
      fontWeight: 700,
    }}
    size={size}
    ml={10}
  >
    <div className="tax-organizer-home-body-item-status">
      <Icon size={15} />
      {text}
    </div>
  </Pill>
);

const currentTaxYear = generateTaxYear();

const TaxOrganizerHome = () => {
  // List store for managing organizers (home page)
  const {
    organizers,
    isLoading,
    error,
    needsRefresh,
    clearNeedsRefresh,
    fetchAllYearsData,
    addOrganizer,
  } = useTaxOrganizerListStore();

  // State for MainHeader and Popover
  const [headerOpened, { toggle: toggleHeader }] = useDisclosure(false);
  const [popoverOpened, { toggle: togglePopover, close: closePopover }] =
    useDisclosure(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // State for Drawer (mobile)
  const [drawerOpened, { open: openDrawer, close: closeDrawer }] =
    useDisclosure(false);

  const navigate = useNavigate();

  // Use useViewportSize to determine screen width
  const { width } = useViewportSize();
  const isMobile = width <= 768;

  // Get userId from auth store
  const userId = useAuthStore((state) => state.user?.id ?? "");

  // Fetch all years data on mount (single optimized call)
  useEffect(() => {
    if (userId) {
      fetchAllYearsData(userId);
    }
  }, [userId, fetchAllYearsData]);

  // Handle refresh when returning from organizer
  useEffect(() => {
    if (needsRefresh && userId) {
      console.log("TaxOrganizerHome - Refresh needed, refetching data");
      fetchAllYearsData(userId);
      clearNeedsRefresh();
    }
  }, [needsRefresh, userId, fetchAllYearsData, clearNeedsRefresh]);

  // Get existing years from store
  const existingYears = useMemo(() => {
    return new Set(organizers.map((org) => org.year));
  }, [organizers]);

  // Function to add a new tax year
  const addTaxYear = (year: number) => {
    try {
      addOrganizer({ year, status: null });
      setErrorMessage(null);
    } catch (error) {
      console.error("Failed to add tax year:", error);
      setErrorMessage("Failed to add tax year");
    }
  };

  const handleStartClick = (year: number) => {
    // Navigate directly to organizer - engagement letter will be checked there
    navigate(`${AppRoutes.taxOrganizer}/${year}`);
  };

  // Generate pool of years available for "Add a return" (current year + 7 previous years)
  const availableYearsPool = useMemo(() => {
    return Array.from({ length: 8 }, (_, i) => currentTaxYear - i);
  }, [currentTaxYear]);

  const getButtonText = (status: OrganizerStatus | null) => {
    if (!status) {
      return "Start";
    }

    const statusLower = status.toLowerCase();

    if (statusLower === "completed" || statusLower === "complete") {
      return "Edit";
    } else if (statusLower === "in progress" || statusLower === "in-progress") {
      return "Continue";
    }

    return "Start";
  };

  // Filter available years to exclude those that already exist
  const availableYears = useMemo(() => {
    return availableYearsPool.filter((year) => !existingYears.has(year));
  }, [availableYearsPool, existingYears]);

  // Years the client has (for export menu)
  const organizerYears = useMemo(
    () => organizers.map((org) => org.year),
    [organizers]
  );
  const { startExport, isExporting } = useClientExport({
    availableYears: organizerYears,
  });

  // Show skeleton while loading
  if (isLoading) {
    return (
      <TaxOrganizerSkeleton
        headerOpened={headerOpened}
        toggleHeader={toggleHeader}
      />
    );
  }

  // Show error state
  if (error) {
    return (
      <AppShell className="client-page" header={{ height: 77 }} padding="md">
        <AppShell.Header className="app-header">
          <MainHeader opened={headerOpened} toggle={toggleHeader} />
        </AppShell.Header>
        <AppShell.Main className="main">
          <div className="tax-organizer-home">
            <Text color="red">An error occurred: {error.message}</Text>
          </div>
        </AppShell.Main>
      </AppShell>
    );
  }

  return (
    <AppShell
      className="client-page"
      header={{ height: 77 }}
      padding="md"
      navbar={{
        width: "15.375rem",
        breakpoint: "sm",
        collapsed: { mobile: !headerOpened }, // Use headerOpened for navbar collapse
      }}
    >
      <AppShell.Header className="app-header">
        <MainHeader
          opened={headerOpened}
          toggle={toggleHeader}
          organizer={false}
        />
      </AppShell.Header>
      <AppShell.Navbar className="nav">
        <Sidebar />
      </AppShell.Navbar>
      <AppShell.Main
        className="main"
        style={{
          paddingInlineStart:
            "calc(var(--app-shell-navbar-offset, 0rem) + var(--app-shell-padding))",
          paddingInlineEnd:
            "calc(var(--app-shell-aside-offset, 0rem) + var(--app-shell-padding))",
        }}
      >
        <div className="tax-organizer-home">
          <div className="tax-organizer-home-header">
            <h2>My Tax Returns</h2>

            {errorMessage && (
              <Text c="red" className="error-message">
                {errorMessage}
              </Text>
            )}

            <Group>
              <Menu position="bottom-end" withArrow disabled={isExporting}>
                <Menu.Target>
                  <Button
                    variant="subtle"
                    size="md"
                    leftSection={<IconDownload size={18} />}
                    rightSection={<IconChevronDown size={14} />}
                    loading={isExporting}
                  >
                    Export
                  </Button>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item
                    onClick={() => startExport("all")}
                    disabled={isExporting}
                  >
                    All years
                  </Menu.Item>
                  {organizerYears.map((year) => (
                    <Menu.Item
                      key={year}
                      onClick={() => startExport(year)}
                      disabled={isExporting}
                    >
                      {year}
                    </Menu.Item>
                  ))}
                </Menu.Dropdown>
              </Menu>
              {isMobile ? (
                <>
                  <Button
                    leftSection={<IconPlus />}
                    size="md"
                    variant="filled"
                    onClick={openDrawer}
                    className="add-return-button-mobile"
                    aria-label="Add a return"
                  >
                    Add a return
                  </Button>

                  <Drawer
                    opened={drawerOpened}
                    onClose={closeDrawer}
                    padding="md"
                    size="100%"
                    zIndex={1000}
                  >
                    <Stack p="md" gap="xl" className="add-return-drawer">
                      <Text size="lg" fw={700}>
                        Select the year
                      </Text>
                      {availableYears.length > 0 ? (
                        availableYears.map((year) => (
                          <Button
                            key={year}
                            variant="outline"
                            className="add-return-button-mobile"
                            fullWidth
                            onClick={() => {
                              addTaxYear(year);
                              closeDrawer(); // Close Drawer after selection
                            }}
                          >
                            {year}
                          </Button>
                        ))
                      ) : (
                        <Text c="gray">No available years to add.</Text>
                      )}
                      <Button
                        variant="subtle"
                        fullWidth
                        onClick={closeDrawer}
                        color="red"
                      >
                        Cancel
                      </Button>
                    </Stack>
                  </Drawer>
                </>
              ) : (
                <Popover
                  width={250}
                  position="bottom"
                  withArrow
                  shadow="md"
                  opened={popoverOpened}
                  onClose={closePopover}
                >
                  <Popover.Target>
                    <Button
                      leftSection={<IconPlus />}
                      size="md"
                      variant="transparent"
                      onClick={togglePopover}
                      aria-label="Add a return"
                    >
                      <Text fw={500}>Add a return</Text>
                    </Button>
                  </Popover.Target>

                  <Popover.Dropdown>
                    <Stack>
                      <Text>Select the year:</Text>
                      {availableYears.length > 0 ? (
                        <Group>
                          {availableYears.map((year) => (
                            <Button
                              key={year}
                              variant="outline"
                              fullWidth
                              onClick={() => {
                                addTaxYear(year);
                                closePopover(); // Close Popover after selection
                              }}
                            >
                              {year}
                            </Button>
                          ))}
                        </Group>
                      ) : (
                        <Text c="gray">No available years to add.</Text>
                      )}
                    </Stack>
                  </Popover.Dropdown>
                </Popover>
              )}
            </Group>
          </div>

          {/* Render all year cards with status */}
          {organizers.map((organizer, index) => {
            const buttonText = getButtonText(organizer.status);
            const buttonVariant = buttonText === "Edit" ? "outline" : "filled";
            const buttonIcon =
              buttonText === "Edit" ? <IconPencil /> : <IconChevronRight />;

            // Get status config for the year's status
            const yearStatus = organizer.status ?? OrganizerStatus.NOT_STARTED;
            const yearStatusConfig = statusConfigs[yearStatus];

            return (
              <Paper
                key={`${organizer.year}-${index}`}
                m="lg"
                className="tax-organizer-home-card"
              >
                <div className="tax-organizer-home-body">
                  <div className="tax-organizer-home-body-item">
                    <h2 className="tax-organizer-home-year">
                      {organizer.year}
                    </h2>
                    {yearStatusConfig && (
                      <Badge
                        icon={yearStatusConfig.icon}
                        text={yearStatusConfig.text}
                        color={yearStatusConfig.color}
                        backgroundColor={yearStatusConfig.backgroundColor}
                      />
                    )}
                  </div>
                  <Button
                    rightSection={buttonIcon}
                    onClick={() => handleStartClick(organizer.year)}
                    variant={buttonVariant}
                  >
                    {buttonText}
                  </Button>
                </div>
              </Paper>
            );
          })}
        </div>

        <MobileButtons />
      </AppShell.Main>
    </AppShell>
  );
};

export default TaxOrganizerHome;
